// @ts-nocheck
import React, { Component } from 'react';
import moment from 'moment';

import Icon from '../Icon/Icon';
import CalendarShortcuts from './CalendarShortcuts';
import CustomText from '../Typography/CustomText/CustomText';

import { bemHelper } from '../../helpers';
import { defaultMonths } from './DatePickerTypes';
import getGlobalProps from '../../helpers/constants/global-props';

export interface CalendarProps {
  value: Date | moment.Moment | string;
  onChange?: (value: Date | moment.Moment | string) => void;
  isOpen?: boolean;
  isCell?: boolean;
  isRightAligned?: boolean;
  dateMask?: 'MM/DD/YYYY' | 'MM/YYYY';
  hasShortcuts?: boolean;
  hasCalendar?: boolean;
  shortcuts: {
    label: React.ReactNode;
    hotkey: string;
    date: Date | moment.Moment | string;
  }[];
  limitStartDate?: Date | moment.Moment | string;
  limitEndDate?: Date | moment.Moment | string;
  closeCalendar: () => void;
  isValidDate: (day: Date | moment.Moment | string, selectedDate: Date | moment.Moment | string) => boolean;
}

interface State {
  fakeMonth: Date | moment.Moment | string;
  fakeYear: Date | moment.Moment | string;
  selectedDate: Date | moment.Moment | string;
  isDayActive: boolean;
}

const globalProps = getGlobalProps({ sunset: true, inkSass: true });

// TODO: Refactor Calendar to be the Panel component, separating Calendar and Shortcuts
export default class Calendar extends Component<CalendarProps, State> {
  static defineHeaderColor(index) {
    return index === 0 || index === 6 ? 'red' : 'gray';
  }

  constructor(props) {
    super(props);

    const fakeMonthWhenNoValueForMonthYearPicker =
      this.props.dateMask === 'MM/YYYY' ? moment().get('month') : undefined;

    const fakeYearWhenNoValueForMonthYearPicker = this.props.dateMask === 'MM/YYYY' ? moment().get('year') : undefined;

    this.state = {
      fakeMonth:
        this.props.value && this.props.dateMask === 'MM/YYYY'
          ? moment(this.props.value, this.props.dateMask).get('month')
          : fakeMonthWhenNoValueForMonthYearPicker,
      fakeYear:
        this.props.value && this.props.dateMask === 'MM/YYYY'
          ? moment(this.props.value, this.props.dateMask).get('year')
          : fakeYearWhenNoValueForMonthYearPicker,
      selectedDate: this.defineInitialValue(),
      isDayActive: false,
    };
    this.onDateClick = this.onDateClick.bind(this);
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const dateFormat = new RegExp(/^(([01]\d)[.\-/\\ ])?([0123]\d)[.\-/\\ ]((19|20)\d{2})/g);
    const { dateMask = 'MM/DD/YYYY' } = this.props;

    if (
      nextProps.value.length >= dateMask.length &&
      dateFormat.test(nextProps.value) &&
      moment(nextProps.value, this.props.dateMask).isValid()
    ) {
      this.setState({
        selectedDate: nextProps.value,
        isDayActive: true,
      });
      if (this.props.dateMask === 'MM/DD/YYYY') {
        this.setState({
          fakeMonth: undefined,
          fakeYear: undefined,
        });
      }
    } else {
      this.setState({
        isDayActive: false,
      });
    }
  }

  onDateClick(day) {
    const { onChange, dateMask, closeCalendar } = this.props;

    closeCalendar();

    this.setState({
      selectedDate: day,
    });

    onChange && onChange(this.getMomentDate(day).format(dateMask));
  }

  getMomentDate(date) {
    return moment(date, this.props.dateMask);
  }

  getCalendarDisplayDate() {
    const realMonth = this.getMomentDate(this.state.selectedDate)
      ? this.getMomentDate(this.state.selectedDate).get('month')
      : moment().get('month');
    const realYear = this.getMomentDate(this.state.selectedDate).get('year') || moment().get('year');

    if (this.props.dateMask === 'MM/YYYY') {
      return moment([realYear, realMonth]);
    }
    return moment([
      this.state.fakeYear ? this.state.fakeYear : realYear,
      this.state.fakeMonth ? this.state.fakeMonth : realMonth,
    ]);
  }

  defineInitialValue() {
    const { value, dateMask } = this.props;
    return value || moment().format(dateMask);
  }

  handleMonthChange(month, setStateCallback?: () => void) {
    const { selectedDate } = this.state;
    const { onChange, dateMask } = this.props;
    const targetMonth = this.getMomentDate(selectedDate).month(month);

    if (dateMask === 'MM/YYYY') {
      onChange && onChange(targetMonth.format(dateMask));
      this.setState(
        {
          selectedDate: targetMonth,
          fakeMonth: month,
        },
        () => {
          if (setStateCallback) setStateCallback();
        },
      );
    } else {
      this.setState(
        {
          fakeMonth: month,
        },
        () => {
          if (setStateCallback) setStateCallback();
        },
      );
    }
  }

  handleYearChange(year) {
    const { selectedDate } = this.state;
    const { onChange, dateMask } = this.props;
    const targetYear = this.getMomentDate(selectedDate).year(year);

    if (dateMask === 'MM/YYYY') {
      onChange && onChange(targetYear.format(dateMask));
      this.setState({
        selectedDate: targetYear,
        fakeYear: year,
      });
    } else {
      this.setState({
        fakeYear: year,
      });
    }
  }

  handleMonthStep(step) {
    const newMonth = this.getCalendarDisplayDate().get('month') + step;

    if (newMonth > 11) {
      this.handleMonthChange('0', () => this.handleYearStep(1));
    } else if (newMonth < 0) {
      this.handleMonthChange('11', () => this.handleYearStep(-1));
    } else {
      this.handleMonthChange(newMonth.toString());
    }
  }

  handleYearStep(step) {
    const newYear = this.getCalendarDisplayDate().get('year') + step;
    this.handleYearChange(newYear.toString());
  }

  defineClassName() {
    const b = bemHelper();

    return b('ink-calendar', {
      open: this.props.isOpen,
      cell: this.props.isCell,
      right: this.props.isRightAligned,
    });
  }

  isBeforeStartLimit(day) {
    return this.getMomentDate(day).isBefore(this.getMomentDate(this.props.limitStartDate), 'day');
  }

  isAfterEndLimit(day) {
    return this.getMomentDate(day).isAfter(this.getMomentDate(this.props.limitEndDate), 'day');
  }

  isDisabledDay(day) {
    return (
      this.isBeforeStartLimit(day) || this.isAfterEndLimit(day) || !this.props.isValidDate(day, this.state.selectedDate)
    );
  }

  isSelectedDay(day) {
    const { selectedDate, isDayActive } = this.state;

    return moment(day).isSame(this.getMomentDate(selectedDate), 'day') && isDayActive && !this.isDisabledDay(day);
  }

  defineDayColor(index, day) {
    if (this.isSelectedDay(day)) {
      return 'white';
    }
    return index === 0 || index === 6 ? 'red' : undefined;
  }

  defineDayClassName(day) {
    const b = bemHelper();

    return b('ink-calendar--table--day', {
      disabled: this.isDisabledDay(day),
      selected: this.isSelectedDay(day),
    });
  }

  renderDayText(index, day) {
    if (moment(day).isSame(this.getMomentDate(moment()), 'day')) {
      return (
        <CustomText as="div" size="small" color={this.defineDayColor(index, day)} weight="bold">
          {moment(day).format('D')}
        </CustomText>
      );
    }
    return (
      <CustomText as="div" size="xsmall" color={this.defineDayColor(index, day)}>
        {moment(day).format('D')}
      </CustomText>
    );
  }

  renderHeader() {
    const startDate = this.getMomentDate(this.state.selectedDate).startOf('week').format();

    const days = Array.from({ length: 7 }).map((v, index) => (
      <th className="ink-calendar--table--header" key={index}>
        <CustomText as="div" size="xsmall" align="center" color={Calendar.defineHeaderColor(index)}>
          {moment(startDate).add(index, 'day').format('ddd')}
        </CustomText>
      </th>
    ));

    return <tr className="ink-calendar--table--head--row">{days}</tr>;
  }

  renderDays() {
    const monthStart = this.getCalendarDisplayDate().startOf('month');
    const monthEnd = this.getCalendarDisplayDate().endOf('month');
    const startDate = this.getMomentDate(monthStart).startOf('week');
    const endDate = this.getMomentDate(monthEnd).endOf('week');
    const rows: React.ReactNode[] = [];
    let day = startDate;
    while (day <= endDate) {
      const days = Array.from({ length: 7 }).map((v, index) => {
        const cloneDay = day;
        day = moment(day).add(1, 'day');
        return (
          <td className="ink-calendar--table--cell" key={String(cloneDay)}>
            <button
              type="button"
              onClick={() => this.onDateClick(cloneDay)}
              className={this.defineDayClassName(cloneDay)}
            >
              {this.renderDayText(index, cloneDay)}
            </button>
          </td>
        );
      });
      rows.push(
        <tr className="ink-calendar--table--body--row" key={String(day)}>
          {days}
        </tr>,
      );
    }
    return <>{rows}</>;
  }

  render() {
    return (
      <div className={this.defineClassName()} {...globalProps}>
        <div className="ink-calendar--body">
          {this.props.hasShortcuts && this.props.isOpen && (
            <div className="ink-calendar--section">
              <CalendarShortcuts
                shortcuts={this.props.shortcuts}
                onDateClick={this.onDateClick}
                closeCalendar={this.props.closeCalendar}
              />
            </div>
          )}
          {this.props.hasCalendar && (
            <div className="ink-calendar--section">
              <div className="ink-calendar--panel">
                <div className="ink-calendar--panel--section ink-calendar--panel--section__month">
                  <button type="button" onClick={() => this.handleMonthStep(-1)} className="ink-calendar--arrow">
                    <Icon type="backward-bold" meaning="previous month" />
                  </button>
                  <div className="ink-calendar--control ink-calendar--control__select">
                    <input
                      className="ink-calendar--control--fake"
                      value={this.getCalendarDisplayDate().format('MMMM')}
                      tabIndex={-1}
                      readOnly
                    />
                    <select
                      className="ink-calendar--control--select"
                      value={this.getCalendarDisplayDate().get('month')}
                      onChange={e => this.handleMonthChange(e.target.value)}
                    >
                      {defaultMonths.map((month, idx) => (
                        <option key={idx} value={idx}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <div className="ink-calendar--control--select-arrow">
                      <Icon type="down" meaning="next month" />
                    </div>
                  </div>
                  <button type="button" onClick={() => this.handleMonthStep(1)} className="ink-calendar--arrow">
                    <Icon type="forward-bold" />
                  </button>
                </div>
                <div className="ink-calendar--panel--section">
                  <button type="button" onClick={() => this.handleYearStep(-1)} className="ink-calendar--arrow">
                    <Icon type="backward-bold" meaning="previous year" />
                  </button>
                  <div className="ink-calendar--control">
                    <input
                      className="ink-calendar--control--input"
                      type="text"
                      maxLength={4}
                      value={this.getCalendarDisplayDate().get('year')}
                      onChange={e => this.handleYearChange(e.target.value)}
                    />
                  </div>
                  <button type="button" onClick={() => this.handleYearStep(1)} className="ink-calendar--arrow">
                    <Icon type="forward-bold" meaning="next year" />
                  </button>
                </div>
              </div>
              {Boolean(this.props.dateMask === 'MM/DD/YYYY') && (
                <table className="ink-calendar--table">
                  <thead>{this.renderHeader()}</thead>
                  <tbody>{this.renderDays()}</tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  static defaultProps = {
    onChange: null,
    isOpen: false,
    isCell: false,
    isRightAligned: false,
    dateMask: 'MM/DD/YYYY',
    limitStartDate: null,
    limitEndDate: null,
  };
}
